import { useState, type ReactNode } from "react";
import type Customer from "@acto/taskpane/core/domain/Customer";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import { AnalyticsEvents, useTrack, type PlanOrigin } from "@acto/taskpane/core/analytics";
import { useOpenCustomer } from "@acto/taskpane/field-sales-2/navigation";
import { toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { useRoutePlanningLightEnabled } from "@acto/taskpane/field-sales-2/data-access/useRoutePlanningLightEnabled";
import { NearbyCustomersSheet } from "./NearbyCustomersSheet";
import { NearbyEntryBanner } from "./NearbyEntryBanner";

interface NearbyCustomersFlow {
  onDayMeetingsChange: (meetings: Meeting[]) => void;
  /** The entry row into the sheet; null while the feature flag is off. */
  entry: ReactNode;
  /** The flow's sheet; the caller must render this node. */
  sheet: ReactNode;
}

export function useNearbyCustomersFlow({
  selectedDay,
  openPlanOnDay,
  openPickerOnDay,
}: {
  selectedDay: Date | null;
  openPlanOnDay: (customer: Customer, date?: Date | null, origin?: PlanOrigin) => void;
  openPickerOnDay: (date?: Date | null, origin?: PlanOrigin) => void;
}): NearbyCustomersFlow {
  const enabled = useRoutePlanningLightEnabled();
  const track = useTrack();
  const openCustomer = useOpenCustomer();

  const [dayMeetings, setDayMeetings] = useState<Meeting[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    track(AnalyticsEvents.NEARBY_CUSTOMERS_OPENED, {
      dayIso: selectedDay ? toIsoDate(selectedDay) : undefined,
      meetingCount: dayMeetings.length,
    });
    setIsOpen(true);
  };

  if (!enabled) return { onDayMeetingsChange: setDayMeetings, entry: null, sheet: null };

  return {
    onDayMeetingsChange: setDayMeetings,
    entry: <NearbyEntryBanner onClick={open} />,
    sheet: (
      <NearbyCustomersSheet
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        day={selectedDay}
        dayMeetings={dayMeetings}
        onPlan={(suggestion) => openPlanOnDay(suggestion.customer, selectedDay, "nearby_suggestion")}
        onOpenCustomer={(customer) => {
          setIsOpen(false);
          openCustomer(customer.id);
        }}
        onSearch={() => openPickerOnDay(selectedDay, "nearby_search")}
      />
    ),
  };
}
